import { useMemo } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';
import api from '../api/api';
import { TimeSpans } from '../../constants/utility/time-spans.constants';
/**
 * Generic cached fetch hook using React Query.
 *
 * @param {string} endpoint - API endpoint (e.g. "/users")
 * @param {object} [params] - Query/filter params (should be memoized)
 * @param {object} [options] - React Query options (staleTime, enabled, etc)
 * @param {object} [axiosConfig] - Additional config for axios (headers, etc)
 * @returns {object} { data, isLoading, isError, error, refetch, ... }
 *
 * @example
 * const params = useMemo(() => ({ page, status }), [page, status]);
 * const { data, isLoading } = useCachedFetch("/users", params);
 */
export function useCachedFetch(endpoint, params = {}, options = {}, axiosConfig = {}) {
    const queryKey = useMemo(() => [endpoint, params], [endpoint, params]);

    const shouldFetch = options.enabled !== undefined ? options.enabled : true;

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            const response = await api.get(endpoint, {
                params,
                ...axiosConfig,
            });
            return response.data;
        },

        staleTime: TimeSpans.FiveMinutes,
        keepPreviousData: true,
        ...options,
        enabled: shouldFetch,
    });

    return query;
}
